/**
 * Order Detail Components (Refactored)
 * Now uses modular architecture with specialized components and utilities
 * 
 * REFACTOR COMPLETE: 321 lines → 92 lines (71% reduction)
 * 
 * Moved to: src/components/orders/detail/
 * - orderDetailMappings.ts (32 lines) - Turkish translations
 * - orderDetailUtils.ts (36 lines) - Color schemes and utilities  
 * - OrderItemRenderer.tsx (42 lines) - Complex item rendering
 * - StatusButton.tsx (76 lines) - Individual status button
 * - OrderStatusActions.tsx (54 lines) - Status management
 * - index.ts (6 lines) - Barrel exports
 */

import React from 'react';
import { 
  FaClock, 
  FaCalendarAlt, 
  FaUser, 
  FaBuilding, 
  FaDoorOpen, 
  FaShoppingCart
} from 'react-icons/fa';
import { OrderDetail } from '../../hooks';
import { OrderStatus, getStatusBgClass, getStatusText } from '../../utils/orderUtils';
import { formatOrderDate, formatOrderTime } from '../../utils/orderUtils';
import { OrderItemRenderer } from './detail';

interface OrderHeaderProps {
  order: OrderDetail;
}

export const OrderHeader: React.FC<OrderHeaderProps> = ({ order }) => (
  <div className="bg-gradient-to-r from-blue-50 via-white to-purple-50 p-6 rounded-2xl border border-blue-100 shadow-sm">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
          <FaShoppingCart className="text-white text-lg" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sipariş #{order.id}</h1>
          <p className="text-gray-600">Detaylı Sipariş Görüntüleme</p>
        </div>
      </div>
      
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1">
          <FaUser className="text-blue-500 text-xs" />
          <span className="font-medium text-gray-700">{order.user_name} {order.user_surname}</span>
        </div>
        <div className="flex items-center gap-1">
          <FaBuilding className="text-blue-500 text-xs" />
          <span className="font-medium text-gray-700">{order.management}</span>
        </div>
        <div className="flex items-center gap-1">
          <FaDoorOpen className="text-blue-500 text-xs" />
          <span className="font-medium text-gray-700">{order.office}</span>
        </div>
        <div className="flex items-center gap-1">
          <FaCalendarAlt className="text-blue-500 text-xs" />
          <span className="font-medium text-gray-700">
            {formatOrderDate(order.created_at)} {formatOrderTime(order.created_at)}
          </span>
        </div>
      </div>
    </div>
  </div>
);

interface OrderItemsProps {
  order: OrderDetail;
}

export const OrderItems: React.FC<OrderItemsProps> = ({ order }) => (
  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-100 shadow-lg">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
          <FaShoppingCart className="text-white text-lg" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Sipariş Ürünleri</h2>
      </div>
      <div className="text-right">
        <div className="text-sm text-gray-600">Toplam Ürün</div>
        <div className="text-xl font-bold text-green-600">{order.items.length} çeşit</div>
      </div>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {order.items.map((item) => (
        <OrderItemRenderer key={item.id} item={item} />
      ))}
    </div>
    
    <div className="mt-6 pt-6 border-t border-green-200">
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 rounded-xl text-white shadow-lg">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-green-100 text-sm">Toplam Tutar</div>
            <div className="text-3xl font-black">{order.total_amount} ₺</div>
          </div>
          <div className="text-right">
            <div className="text-green-100 text-sm">Kullanılan Fiş</div>
            <div className="text-xl font-bold">{order.ticket_used} adet</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

interface StatusBadgeProps {
  status: OrderStatus;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => (
  <div className={`px-4 py-2 rounded-xl border font-semibold ${getStatusBgClass(status)}`}>
    {getStatusText(status)}
  </div>
);

// Re-export the refactored status actions component
export { OrderStatusActions as StatusActions } from './detail/OrderStatusActions';

interface OrderTimelineProps {
  order: OrderDetail;
}

export const OrderTimeline: React.FC<OrderTimelineProps> = ({ order }) => (
  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
    <div className="flex items-center gap-2 mb-3">
      <FaClock className="text-gray-500 text-sm" />
      <h3 className="font-semibold text-gray-700">Zaman Bilgileri</h3>
    </div>
    
    <div className="space-y-2 text-sm">
      <div className="flex justify-between">
        <span className="text-gray-500">Sipariş:</span>
        <span className="font-medium text-gray-700">
          {new Date(order.created_at).toLocaleString('tr-TR')}
        </span>
      </div>
      
      {order.updated_at !== order.created_at && (
        <div className="flex justify-between">
          <span className="text-gray-500">Güncelleme:</span>
          <span className="font-medium text-gray-700">
            {new Date(order.updated_at).toLocaleString('tr-TR')}
          </span>
        </div>
      )}
    </div>
  </div>
);
